import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const SentimentOverview = ({ data }) => {
  const total = Object.values(data).reduce((acc, val) => acc + val, 0);
  
  // Format data for chart
  const chartData = [
    { name: 'Promoter', value: data.Promoter || 0, percentage: Math.round(((data.Promoter || 0) / total) * 100) },
    { name: 'Passive', value: data.Passive || 0, percentage: Math.round(((data.Passive || 0) / total) * 100) },
    { name: 'Detractor', value: data.Detractor || 0, percentage: Math.round(((data.Detractor || 0) / total) * 100) }
  ];
  
  // Custom colors for sentiment types
  const COLORS = {
    Promoter: '#22c55e',
    Passive: '#94a3b8',
    Detractor: '#ef4444'
  };
  
  // Tooltip formatter
  const tooltipFormatter = (value, name, props) => {
    return [`${value} (${props.payload.percentage}%)`, name];
  };
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium">{payload[0].payload.name}</p>
          <p className="text-sm">
            <span className="font-semibold">{payload[0].value}</span> responses
            <span className="ml-1 text-gray-500">({payload[0].payload.percentage}%)</span>
          </p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 h-full"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Rating Distribution</h3>
          <p className="text-sm text-gray-500">Overview of customer feedback sentiments</p>
        </div>
      </div>
      
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#d1d5db" strokeWidth={1} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[4, 4, 0, 0]}
              barSize={60}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mt-8">
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mb-1"></div>
          <div className="text-sm font-medium">Promoters</div>
          <div className="text-gray-500 text-xs">{data.Promoter || 0} responses</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-gray-400 mb-1"></div>
          <div className="text-sm font-medium">Passives</div>
          <div className="text-gray-500 text-xs">{data.Passive || 0} responses</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-red-500 mb-1"></div>
          <div className="text-sm font-medium">Detractors</div>
          <div className="text-gray-500 text-xs">{data.Detractor || 0} responses</div>
        </div>
      </div>
    </motion.div>
  );
};

export default SentimentOverview;
